import { getFileUrl } from '@/shared/utilities/file-helper';
import { useQuery } from '@tanstack/react-query';
import { attachmentKeys } from './attachment-keys';

/**
 * Custom hook to fetch and cache an image URL from an attachment URL using React Query.
 *
 * This hook utilizes `useQuery` to retrieve the image URL by calling `getFileUrl` with the provided `attachmentUrl`.
 * It includes caching with a stale time of 5 minutes and garbage collection time of 10 minutes, with a single retry on failure.
 * The query is only enabled if `attachmentUrl` is provided.
 *
 * @param attachmentUrl - The URL of the attachment from which to derive the image URL. If undefined, the query is disabled.
 * @returns An object containing:
 *   - `imageUrl`: The fetched image URL as a string, or undefined if not available.
 *   - `isLoading`: Boolean indicating if the query is currently loading.
 *   - `isError`: Boolean indicating if the query encountered an error.
 *   - `error`: The error object if an error occurred, otherwise undefined.
 */
export function useAttachmentUrl(attachmentUrl?: string, enabled = true, bucketName?: string) {
  // Create conversation-scoped query key

  const query = useQuery({
    queryKey: attachmentKeys.url(attachmentUrl),
    queryFn: async (): Promise<string | undefined> => {
      if (!attachmentUrl) return undefined;
      return await getFileUrl(attachmentUrl, bucketName);
    },
    enabled: !!attachmentUrl && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1
  });

  return {
    blobUrl: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error
  };
}
